import { useTranslation } from "react-i18next";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  videoUrl: string;
}

function getEmbedUrl(url: string): string | null {
  if (!url) return null;
  
  const cleanedUrl = url.trim();

  // If already an embed URL, return as is
  if (cleanedUrl.includes("/embed/")) {
    return cleanedUrl;
  }

  let videoId = "";
  try {
    if (cleanedUrl.includes("youtube.com/watch")) {
      const urlObj = new URL(cleanedUrl);
      videoId = urlObj.searchParams.get("v") || "";
    } else if (cleanedUrl.includes("youtube.com/shorts/")) {
      const parts = cleanedUrl.split("/shorts/");
      if (parts[1]) {
        videoId = parts[1].split(/[?#]/)[0];
      }
    } else if (cleanedUrl.includes("youtu.be/")) {
      const parts = cleanedUrl.split("youtu.be/");
      if (parts[1]) {
        videoId = parts[1].split(/[?#]/)[0];
      }
    }
  } catch (e) {
    console.error("Failed to parse video URL:", e);
  }

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  }

  return null;
}

export default function VideoModal({ isOpen, onClose, title, videoUrl }: VideoModalProps) {
  const { t, i18n } = useTranslation("common");
  const isRtl = i18n.dir() === "rtl";

  if (!isOpen) return null;

  const embedUrl = getEmbedUrl(videoUrl);
  const isDirectVideo =
    videoUrl &&
    (videoUrl.toLowerCase().endsWith(".mp4") ||
      videoUrl.toLowerCase().endsWith(".webm") ||
      videoUrl.toLowerCase().endsWith(".ogg"));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl animate-fade-in-up z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-100">
          <h3 className="text-base md:text-lg font-semibold text-navy-400 truncate pr-4">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        {/* Video Player */}
        <div className="aspect-video bg-black flex items-center justify-center relative">
          {isDirectVideo ? (
            <video
              src={videoUrl}
              controls
              autoPlay
              playsInline
              className="w-full h-full object-contain"
            />
          ) : embedUrl ? (
            <iframe
              src={embedUrl}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full absolute inset-0"
            />
          ) : (
            <div className="text-center p-6 bg-gray-900 w-full h-full flex flex-col justify-center items-center text-white">
              <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mb-3">
                <i className="ri-error-warning-line text-red-500 text-2xl" />
              </div>
              <p className="text-sm font-medium">
                {isRtl ? "رابط الفيديو غير مدعوم للتشغيل المباشر" : "Video link not supported for direct play"}
              </p>
              <p className="text-xs text-gray-400 mt-1 max-w-md truncate">
                {videoUrl}
              </p>
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 px-4 py-2 bg-primary-400 hover:bg-primary-500 text-white text-xs font-semibold rounded-full transition-colors inline-flex items-center gap-1.5"
              >
                <span>{isRtl ? "فتح الرابط الخارجي" : "Open external link"}</span>
                <i className="ri-external-link-line" />
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 md:p-5 bg-gray-50 border-t border-gray-100">
          <p className="text-sm text-gray-600 leading-relaxed font-medium">
            {title}
          </p>
        </div>
      </div>
    </div>
  );
}